import React, { createContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

// Types
import {
  isCloudLinkedItem,
  Item,
  ITEM_TYPE_DOCUMENT,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LABELS,
  ITEM_TYPE_LINK,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import { Subscription } from "@/models/subscriptions/Subscription";
import { SimpleFavoriteDocuments } from "@/models/users/User";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import {
  BusyAction,
  PathSegmentDropTarget,
  useFilesTabItemDragAndDrop,
} from "../hooks/useFilesTabItemDragAndDrop";
import { buildRenamedFolderPath } from "../utils/folderRename";
import AppRoutes from "@/constants/routes/AppRoutes";

// Hooks
import useFileSync from "../hooks/useFileSync";
import useWorkspace from "@/hooks/useWorkspace";
import useUsers from "@/hooks/useUsers";
import useGroups from "@/hooks/useGroups";
import { useWorkspaceContext } from "./WorkspaceContext";
import { useFilesTabFileOperations } from "../hooks/useFilesTabFileOperations";
import { useFilesTabDialogs } from "../hooks/useFilesTabDialogs";
import { useFilesTabNavigation } from "../hooks/useFilesTabNavigation";
import { UseMutationResult } from "@tanstack/react-query";
import { ReindexDocumentResult } from "@/services/workspaceServices";
import Workspace from "@/models/workspace/Workspace";

/**
 * Context value type for all file operations
 */
export type FileOperationsContextValue = {
  // Workspace data
  workspaceId: string;
  favorites: SimpleFavoriteDocuments;
  subscriptions: Subscription[];
  /** Entity paths (workspace/workspace-document) that any of the current user's groups is subscribed to. */
  groupSubscriptionEntityPaths: Set<string>;

  // Drag & Drop state
  draggedItem: string | null;
  dropTarget: string | null;
  pathSegmentDropTarget: PathSegmentDropTarget;

  // Item Busy state
  itemBusy: Record<string, boolean>;
  busyReason: Record<string, string>;
  setBusy: (itemId: string, action: BusyAction) => void;
  clearBusy: (itemId: string) => void;

  // Drag & Drop handlers
  handleDragStart: (e: React.DragEvent, id: string) => void;
  handleDragOver: (e: React.DragEvent, id: string) => void;
  handleDragLeave: () => void;
  handleDrop: (
    e: React.DragEvent,
    targetId: string,
    draggedItem: string
  ) => Promise<void>;
  handlePathSegmentDragOver: (targetFolderId: string | "root") => void;
  handlePathSegmentDragLeave: () => void;
  handleDropOnPathSegment: (
    e: React.DragEvent,
    targetFolderId: string | null
  ) => Promise<void>;

  // UI state
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;

  // Navigation handlers
  handleRowClick: (item: Item, event?: React.MouseEvent) => void;

  // File operations
  handleDownloads: (
    e: React.MouseEvent,
    items: Item[],
    version?: number
  ) => Promise<void>;
  onSyncToSharePointClick: (item: Item) => void;

  // File sync operations
  documentsSynchronizing: string[];
  handleSharePointFileSyncClick: (
    document: WorkspaceDocument
  ) => Promise<{ variant: string; description: string; title: string }>;
  handleSyncFolder: (folderId: string) => Promise<void>;
  handleUnlinkFolder: (folderId: string) => Promise<void>;

  // Dialog state
  setOpenEditDocumentDialog: (item: Item | null) => void;
  setWordInformationDialogOpen: (open: boolean) => void;
  setWordInformationItem: (item: Item | null) => void;
  openEditDocumentDialog: Item | null;
  wordInformationDialogOpen: boolean;
  wordInformationItem: Item | null;
  isSyncingToSharePoint: boolean;
  setIsSyncingToSharePoint: (syncing: boolean) => void;
  syncDocument: WorkspaceDocument | null;
  moveModalItem: Item | null;
  setMoveModalItem: (item: Item | null) => void;
  renameFolderItem: Item | null;
  setRenameFolderItem: (item: Item | null) => void;
  renameDocumentItem: Item | null;
  setRenameDocumentItem: (item: Item | null) => void;
  /** The link whose name/address is being edited; null when the dialog is closed. */
  editLinkItem: Item | null;
  setEditLinkItem: (item: Item | null) => void;
  /**
   * Rewrites the files URL to use `newName` for `folderId` when that folder is
   * on the current path. Any action that changes a folder's name (rename, or
   * OneDrive/SharePoint relink, which overwrites the name with the remote
   * folder's) must call this or the URL desyncs from the renamed folder.
   */
  syncPathAfterFolderRename: (folderId: string, newName: string) => void;

  // Permission checks
  canUserEditMetadata: (item: Item) => boolean;
  canUserDeleteFile: (item: Item) => boolean;
  canUserMoveItems: () => boolean;
  canItemBeReIndexed: (item: Item) => boolean;

  // Delete operations
  handleDeleteDocuments: (
    workspaceId: string,
    documentIds: string[]
  ) => Promise<void>;
  deleteFolder: UseMutationResult<
    Workspace,
    Error,
    { workspaceId: string; folderId: string },
    unknown
  >;
  deleteDialogItem: Item | null;
  isDeleteDialogOpen: boolean;
  isDeleteDialogPending: boolean;
  openDeleteDialog: (item: Item) => void;
  closeDeleteDialog: () => void;
  confirmDeleteDialog: () => Promise<void>;

  // Reindex operation
  reindexDocument: UseMutationResult<
    ReindexDocumentResult,
    Error,
    {
      workspaceId: string;
      documentId: string;
    }
  >;

  // Helper functions
  isItemCloudSource: (item: Item) => boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const FileOperationsContext =
  createContext<FileOperationsContextValue | null>(null);

/**
 * Props for the FileOperationsProvider
 */
type FileOperationsProviderProps = {
  children: ReactNode;
  currentItems: Item[];
  currentPath: { id: string; name: string }[];
  openFolderId?: string;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
};

/**
 * Provider component that wraps FilesTab content and provides all file operation
 * state and handlers to child components via context.
 *
 * This eliminates prop drilling through FileList, FileGrid, and FileItemDropdownMenu.
 */
export const FileOperationsProvider: React.FC<FileOperationsProviderProps> = ({
  children,
  currentItems,
  currentPath,
  openFolderId,
  setSelectedItems,
}) => {
  const navigate = useNavigate();

  const {
    workspace,
    canUserDeleteFile,
    canUserEditMetadata,
    canUserMoveItems,
    handleDeleteDocuments,
    canItemBeReIndexed,
  } = useWorkspaceContext();
  const workspaceId = workspace!.id;

  // Queries for favorites and subscriptions
  const { useQueryUsersFavoriteDocuments, useQuerySubscriptions } = useUsers();
  const { useMyGroupsSubscriptionEntityPaths } = useGroups();
  const fetchedFavoriteDocuments = useQueryUsersFavoriteDocuments();
  const fetchedSubscriptions = useQuerySubscriptions();
  const { groupSubscriptionEntityPaths } =
    useMyGroupsSubscriptionEntityPaths();

  // Local state for favorites and subscriptions
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [favorites, setFavorites] = useState<SimpleFavoriteDocuments>({
    workspace: [],
    legislation: [],
  });

  // UI state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Dialog state
  const {
    openEditDocumentDialog,
    setOpenEditDocumentDialog,
    isSyncingToSharePoint,
    setIsSyncingToSharePoint,
    syncDocument,
    setSyncDocument,
    wordInformationDialogOpen,
    setWordInformationDialogOpen,
    wordInformationItem,
    setWordInformationItem,
    renameFolderItem,
    setRenameFolderItem,
    renameDocumentItem,
    setRenameDocumentItem,
    editLinkItem,
    setEditLinkItem,
  } = useFilesTabDialogs();
  const [moveModalItem, setMoveModalItem] = useState<Item | null>(null);
  const [deleteDialogItem, setDeleteDialogItem] = useState<Item | null>(null);
  const [isDeleteDialogPending, setIsDeleteDialogPending] = useState(false);

  // Navigation
  const { handleRowClick } = useFilesTabNavigation({
    workspace,
    workspaceId,
    openFolderId,
    setSelectedItems,
  });

  // Drag and drop
  const {
    draggedItem,
    dropTarget,
    pathSegmentDropTarget,
    itemBusy,
    busyReason,
    setBusy,
    clearBusy,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePathSegmentDragOver,
    handlePathSegmentDragLeave,
    handleDropOnPathSegment,
  } = useFilesTabItemDragAndDrop({
    workspaceId,
    currentItems,
    workspace,
    canMoveItems: canUserMoveItems(),
  });

  // File operations
  const { handleDownloads, onSyncToSharePointClick } =
    useFilesTabFileOperations({
      workspace,
      setIsSyncingToSharePoint,
      setSyncDocument,
    });

  // File sync operations
  const {
    documentsSynchronizing,
    handleSharePointFileSyncClick,
    handleSyncFolder,
    handleUnlinkFolder,
  } = useFileSync();

  // Workspace mutations
  const { deleteFolder, deleteLink, reindexDocument } = useWorkspace();

  // Effect: Update subscriptions when fetched
  useEffect(() => {
    if (fetchedSubscriptions.isFetched && fetchedSubscriptions.data) {
      setSubscriptions(fetchedSubscriptions.data);
    }
  }, [fetchedSubscriptions.isFetched, fetchedSubscriptions.data]);

  // Effect: Update favorites when fetched
  useEffect(() => {
    if (fetchedFavoriteDocuments.isFetched && fetchedFavoriteDocuments.data) {
      setFavorites({
        workspace: fetchedFavoriteDocuments.data.favoriteWorkspaceDocuments.map(
          (fav) => fav.documentId
        ),
        legislation: fetchedFavoriteDocuments.data.favoriteLegislations.map(
          (fav) => fav.externalId
        ),
      });
    }
  }, [fetchedFavoriteDocuments.isFetched, fetchedFavoriteDocuments.data]);

  // Kept under its long-standing context name; the rule itself lives in
  // workspaceItemUtils so the rename dialog and the menu agree on it.
  const isItemCloudSource = isCloudLinkedItem;

  const syncPathAfterFolderRename = (folderId: string, newName: string) => {
    const renamedPath = buildRenamedFolderPath(currentPath, folderId, newName);
    if (renamedPath) {
      navigate(AppRoutes.workspaceFilesFolder(workspaceId, renamedPath), {
        replace: true,
      });
    }
  };

  const openDeleteDialog = (item: Item) => {
    setDeleteDialogItem(item);
  };

  const closeDeleteDialog = () => {
    if (isDeleteDialogPending) return;
    setDeleteDialogItem(null);
  };

  const confirmDeleteDialog = async () => {
    if (!deleteDialogItem) return;

    setIsDeleteDialogPending(true);
    setBusy(deleteDialogItem.id, "delete");

    try {
      if (deleteDialogItem.type === ITEM_TYPE_DOCUMENT) {
        await handleDeleteDocuments(workspaceId, [deleteDialogItem.id]);
      }

      if (deleteDialogItem.type === ITEM_TYPE_FOLDER) {
        await deleteFolder.mutateAsync({
          workspaceId,
          folderId: deleteDialogItem.id,
        });
        showSuccessToast("Mappen er blevet slettet");
      }

      if (deleteDialogItem.type === ITEM_TYPE_LINK) {
        await deleteLink.mutateAsync({
          workspaceId,
          linkId: deleteDialogItem.id,
        });
        showSuccessToast("Linket er blevet slettet");
      }

      setDeleteDialogItem(null);
    } catch (error) {
      logger.error("Failed to delete workspace item", {
        operation: "deleteWorkspaceItem",
        error,
        workspaceId,
        itemId: deleteDialogItem.id,
        itemType: deleteDialogItem.type,
      });
      showErrorToast(
        `Kunne ikke slette ${ITEM_TYPE_LABELS[deleteDialogItem.type].definite}. Prøv igen.`
      );
    } finally {
      clearBusy(deleteDialogItem.id);
      setIsDeleteDialogPending(false);
    }
  };

  const contextValue: FileOperationsContextValue = {
    // Workspace data
    workspaceId,
    favorites,
    subscriptions,
    groupSubscriptionEntityPaths: groupSubscriptionEntityPaths ?? new Set(),

    // Drag & Drop state
    draggedItem,
    dropTarget,
    pathSegmentDropTarget,

    // Item Busy state
    itemBusy,
    busyReason,
    setBusy,
    clearBusy,

    // Drag & Drop handlers
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePathSegmentDragOver,
    handlePathSegmentDragLeave,
    handleDropOnPathSegment,

    // UI state
    openDropdownId,
    setOpenDropdownId,

    // Navigation handlers
    handleRowClick,

    // File operations
    handleDownloads,
    onSyncToSharePointClick,

    // File sync operations
    documentsSynchronizing,
    handleSharePointFileSyncClick,
    handleSyncFolder,
    handleUnlinkFolder,

    // Dialog state
    openEditDocumentDialog,
    setOpenEditDocumentDialog,
    wordInformationDialogOpen,
    setWordInformationDialogOpen,
    wordInformationItem,
    setWordInformationItem,
    isSyncingToSharePoint,
    setIsSyncingToSharePoint,
    syncDocument,
    moveModalItem,
    setMoveModalItem,
    renameFolderItem,
    setRenameFolderItem,
    renameDocumentItem,
    setRenameDocumentItem,
    editLinkItem,
    setEditLinkItem,
    syncPathAfterFolderRename,

    // Permission checks
    canUserEditMetadata,
    canUserDeleteFile,
    canUserMoveItems,
    canItemBeReIndexed,

    // Delete operations
    handleDeleteDocuments,
    deleteFolder,
    deleteDialogItem,
    isDeleteDialogOpen: !!deleteDialogItem,
    isDeleteDialogPending,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDeleteDialog,

    // Reindex operation
    reindexDocument,

    // Helper functions
    isItemCloudSource,
  };

  return (
    <FileOperationsContext.Provider value={contextValue}>
      {children}
    </FileOperationsContext.Provider>
  );
};
